/**
 * SONGS domain state — the library, its filters, and version management.
 *
 * SIMULATED. Songs are in-memory and reset on reload; generation is a timer (see
 * src/api/generationApi.ts). No audio exists for any song.
 *
 * Ordering: songs carry an explicit `seq` counter rather than being sorted by id,
 * because ids are opaque UUID strings. The counter lives in this store so a
 * single monotonic sequence is shared by every creation path (Create, Genre
 * Mixer, duplicate).
 */
import { create } from 'zustand';
import { USE_MOCK_API } from '../config';
import { songsApi } from '../api/songsApi';
import { shortDate } from '../lib/formatting';
/*
  THE SEED COMES FROM THE MOCK, NOT FROM THE FIXTURE FACTORY, and the distinction is not
  cosmetic. `createInitialSongs()` mints fresh ids on every call, so seeding here and seeding
  the mock's songs table separately produced two libraries describing the same four songs
  under DIFFERENT ids. That was invisible until the public reads started querying the table
  (Batch 7 §1.6): publishing a song would then patch an id the table had never seen, and the
  Home page would stay empty while the Library said the track was discoverable.

  With a backend this line goes away entirely — the store starts empty and `loadSongs()` fills
  it from `GET /songs` behind the read states that exist for exactly that. See
  api/mockCatalogue.mockSeedLibrary.
*/
import { mockSeedLibrary } from '../api/mockCatalogue';
import { reconcileGeneratedSong, shouldRefreshGeneratedSong } from '../hooks/useSongs';
import { newId } from '../lib/ids';
import { hydratePersistedSongLengths } from '../lib/songRuntime';
import type {
  Song,
  SongFilters,
  SongSortOrder,
  SongVersion,
  SongVersionKind,
  SongVisibility,
} from '../types/domain';
import { IDLE_LOAD, LOADING_LOAD, READY_LOAD, failedLoad, type LoadState } from './loadState';

const DEFAULT_FILTERS: SongFilters = {
  search: '',
  genre: 'All genres',
  sortOrder: 'newest',
  favoritesOnly: false,
};

function highestSongSeq(songs: readonly Song[]): number {
  return songs.reduce((max, song) => Math.max(max, song.seq), 0);
}

function songCreatedAtValue(song: Song): number | null {
  if (!song.createdAt) return null;
  const parsed = Date.parse(song.createdAt);
  return Number.isFinite(parsed) ? parsed : null;
}

function compareSongsByOrder(a: Song, b: Song, order: SongSortOrder): number {
  const aDate = songCreatedAtValue(a);
  const bDate = songCreatedAtValue(b);

  // If either song carries a real `createdAt`, prefer generation-time ordering.
  // When both have valid dates compare by them. When only one has a date treat the
  // missing value as an extreme so dated songs consistently order before/after
  // undated ones rather than falling back to `seq` in mixed comparisons.
  if (aDate !== null || bDate !== null) {
    const aVal = aDate ?? (order === 'newest' ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY);
    const bVal = bDate ?? (order === 'newest' ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY);
    return order === 'newest' ? bVal - aVal : aVal - bVal;
  }

  // Neither song has a createdAt — fall back to the monotonic `seq` counter.
  return order === 'newest' ? b.seq - a.seq : a.seq - b.seq;
}

export type GenerationSurface = 'create' | 'imageMusic';

interface SongsState {
  songs: Song[];
  filters: SongFilters;
  /** Version explicitly loaded as the source for the next direct audio edit, or null. */
  editingHeadVersionId: string | null;
  /** Monotonic ordering counter. Higher = newer. */
  seq: number;
  /** True while a generation is in flight. */
  generating: boolean;
  /** Which route surface owns the current generation UI, if any. */
  activeGenerationSurface: GenerationSurface | null;
  /**
   * Where the library's READ has got to. See stores/loadState.ts.
   *
   * Separate from `generating`, which is a WRITE state — a generation in flight — and
   * describes something entirely different: `generating` is true while a song is being
   * made, this is true while the list of songs is being fetched. Collapsing them would
   * mean the Create button's spinner and the library's skeleton were one boolean.
   *
   * `'idle'` at rest, because nothing has been fetched: the store is SEEDED from the
   * fixture at construction (see `initialSongs` below), which is not the same thing as
   * having loaded it. That distinction is the reason this starts idle rather than ready.
   */
  load: LoadState;

  /** Next ordering key. Increments the counter. */
  nextSeq: () => number;
  addSong: (song: Song) => void;
  /** Inserts a canonical song returned by the server and advances seq to match. */
  insertSong: (song: Song) => void;
  setGenerating: (generating: boolean) => void;
  setActiveGenerationSurface: (surface: GenerationSurface | null) => void;
  updateSong: (id: string, patch: Partial<Song>) => void;
  deleteSong: (id: string) => void;
  /** Duplicates a song through the API and inserts the returned copy. */
  duplicateSong: (song: Song) => Promise<Song>;
  toggleFavorite: (id: string) => void;
  /**
   * Sets a song's visibility — the ONLY way `visibility` ever changes.
   *
   * THE PUBLISH BOUNDARY, and it is now in use: the Library's "Make discoverable" action
   * calls it with `'public'` and "Remove from catalogue" calls it with `'private'`, both
   * through hooks/useSongs. It existed before either did so that a publication decision would
   * have exactly one path to travel rather than a `updateSong(id, { visibility })` call
   * appearing in whichever component grew the button — which is what makes the rule auditable
   * now that there are two callers instead of none.
   *
   * RETURNS WHETHER THE SERVER ACTUALLY ACCEPTED IT. The set is optimistic — this resolves
   * `true` or `false` after the fact, not before — but a caller that ignores the return value
   * still gets the same immediate, synchronous local update it always did, because that `set`
   * runs before the first `await`. What changed is that a rejected PATCH (an expired
   * session, a lapsed purchase, a real server error) now rolls the local value back to what
   * it was instead of leaving the UI claiming a publication the database never recorded — a
   * share link built from that lie would 404 for every visitor while the sharer's own screen
   * still showed it as live. See `hooks/useSongs.makeDiscoverable`, the caller this matters
   * for most.
   *
   * Generation must never call it. See types/domain.SongVisibility.
   */
  setVisibility: (id: string, visibility: SongVisibility) => Promise<boolean>;
  /** Records a download against a song's counter. */
  recordDownload: (id: string, format?: string) => void;
  /**
   * Records a play against a song's counter once the run clears the listening threshold.
   *
   * Mirrors `recordDownload` deliberately: same shape, same seam, one counter each. The
   * caller is the player (see hooks/usePlayer), so every surface that reaches 30 listened
   * seconds counts it, rather than each card having to remember to.
   */
  recordPlay: (id: string) => void;
  /**
   * Records a share against a song's counter, once a share actually left the app.
   *
   * Mirrors `recordPlay`: same shape, same seam, one counter each. The caller is
   * hooks/useShareSong — the app's ONE share action — so every surface that shares counts
   * it, and a dismissed or failed share counts nothing because nothing was shared.
   */
  recordShare: (id: string) => void;

  /** Appends a version. Returns its display label, which the caller may toast. */
  addVersion: (
    songId: string,
    label: string,
    kind: SongVersionKind,
    prompt?: string,
    audioUrl?: string | null,
    providerClipId?: string,
    createdAt?: string,
    lyrics?: string,
    lyricsTimedRaw?: Song['lyricsTimedRaw'],
    lyricTimings?: Song['lyricTimings'],
    lyricSectionTimings?: Song['lyricSectionTimings']
  ) => string;
  /**
   * Copies a version, inserting the copy after the original. Returns its label, or ''.
   *
   * The one operation §7 asks for that Version History did not have. It is a record copy,
   * not an audio copy, and there is nothing to fake about that: a version carries a label, a
   * kind, a length, a date and the prompt that produced it, and duplicating those five
   * fields is entirely honest. `audioUrl` is copied too and is null on every version there
   * has ever been, so the copy is as complete as the original.
   *
   * WHY IT IS USEFUL WITHOUT AUDIO: it is how you keep a version's settings before renaming
   * or restoring it, which is a real thing to want from a list whose entries are labels.
   *
   * Inserted after the original rather than appended, unlike addVersion: a copy belongs
   * beside the thing it copies, and appending would put it after versions made later.
   */
  duplicateVersion: (songId: string, versionId: string) => string;
  /** Promotes one hidden version-backed Song into the visible library. */
  saveVersionToLibrary: (songId: string, versionId: string) => Promise<Song | null>;
  deleteVersion: (songId: string, versionId: string) => void;
  renameVersion: (songId: string, versionId: string, label: string) => void;
  /** Marks which version future edits should use as their source. */
  loadVersionForEditing: (songId: string, versionId: string) => SongVersion | null;

  setFilters: (patch: Partial<SongFilters>) => void;
  resetFilters: () => void;
  /**
   * Fetches the library through `songsApi.getSongs()` and replaces what is held.
   *
   * ==========================================================================
   * THE READ THAT DID NOT EXIST. `songsApi.getSongs()` had no caller anywhere in the
   * app: the store seeded itself from `createInitialSongs()` directly, so the fixture
   * was the STORE'S INITIAL STATE rather than the MOCK'S RESPONSE. That is the seam
   * defect Batch 7 §1 is about — a backend arriving would have had to change this
   * store, not just the API module, because there was no fetch to redirect.
   *
   * Now the fixture is what the mock returns and this is the one path it arrives by.
   * `getSongs()` becomes a `fetch` and nothing here changes.
   * ==========================================================================
   *
   * WHAT IT DOES NOT DO: it is not called on mount by anything. Nothing in the app
   * calls it yet, and that is deliberate rather than unfinished — the store is already
   * seeded with the same fixture the mock returns, so a load on boot would replace four
   * songs with four identical songs behind a spinner, and every song created in the
   * session would be lost to a later call. §32: "Do not claim an API is implemented
   * merely because an interface exists" — so this is stated plainly rather than wired up
   * to look connected. It is called by tests, and by the backend integration that
   * replaces the seed with a real fetch.
   *
   * Never rejects. Failures land in `load.error` for a screen to render, because a
   * rejected promise from a store action is a promise every caller has to catch.
   */
  loadSongs: () => Promise<void>;
  /** Replaces one song with the server's latest copy. */
  replaceSong: (song: Song) => void;
  /** Replaces the library, e.g. from a future server fetch. */
  hydrate: (songs: Song[]) => void;
  reset: () => void;
}

const initialSongs = USE_MOCK_API ? hydratePersistedSongLengths(mockSeedLibrary()) : [];

export const useSongsStore = create<SongsState>((set, get) => ({
  songs: initialSongs,
  filters: { ...DEFAULT_FILTERS },
  editingHeadVersionId: null,
  seq: highestSongSeq(initialSongs),
  generating: false,
  activeGenerationSurface: null,
  load: IDLE_LOAD,

  nextSeq() {
    const next = get().seq + 1;
    set({ seq: next });
    return next;
  },

  addSong(song) {
    set((s) => ({ songs: [song, ...s.songs] }));
  },

  insertSong(song) {
    set((s) => ({
      songs: [song, ...s.songs],
      seq: Math.max(s.seq, song.seq),
    }));
  },

  replaceSong(song) {
    set((s) => {
      const current = s.songs.find((candidate) => candidate.id === song.id);
      const addedVersion = (song.versions.length ?? 0) > (current?.versions.length ?? 0);
      return {
        songs: s.songs.map((candidate) => (candidate.id === song.id ? song : candidate)),
        // Version History is the draft area now, so when an edit appends a new version we
        // keep working on THAT latest draft instead of dropping the edit target back to the
        // visible parent song's original head.
        editingHeadVersionId: addedVersion ? (song.versions.at(-1)?.id ?? null) : s.editingHeadVersionId,
      };
    });
  },

  setGenerating(generating) {
    set({ generating });
  },

  setActiveGenerationSurface(activeGenerationSurface) {
    set({ activeGenerationSurface });
  },

  updateSong(id, patch) {
    set((s) => ({ songs: s.songs.map((song) => (song.id === id ? { ...song, ...patch } : song)) }));
    void songsApi.updateSong(id, patch);
  },

  deleteSong(id) {
    set((s) => ({ songs: s.songs.filter((song) => song.id !== id) }));
    void songsApi.deleteSong(id);
  },

  async duplicateSong(song) {
    /*
      THE SERVER'S COPY IS THE COPY. The duplicate route already resets the fields that make a
      copy its own song — title, seq, publication, engagement counters and version ids — and it
      returns the canonical row. Using that answer keeps the Library, the server and every later
      song lookup on the same id, which is the difference between a real duplicate and a local
      placeholder that the backend has never heard of.
    */
    const copy = await songsApi.duplicateSong(song.id);
    get().insertSong(copy);
    return copy;
  },

  toggleFavorite(id) {
    const next = !get().songs.find((s) => s.id === id)?.favorite;
    set((s) => ({
      songs: s.songs.map((song) => (song.id === id ? { ...song, favorite: next } : song)),
    }));
    void songsApi.setFavorite(id, next);
  },

  async setVisibility(id, visibility) {
    const previous = get().songs.find((song) => song.id === id)?.visibility;
    if (previous === undefined) return false;

    set((s) => ({
      songs: s.songs.map((song) => (song.id === id ? { ...song, visibility } : song)),
    }));
    /*
      Through the same PATCH the rest of the store's edits use, so the intent reaches the
      seam rather than staying in memory (`server/src/routes/songs.ts` persists it for real).
      AWAITED AND ROLLED BACK ON FAILURE, unlike this store's other fire-and-forget writes
      (`toggleFavorite`, `recordPlay`, ...): those move a counter or a flag, where a lost
      write is a stale number nobody will notice. This one decides whether a share link
      resolves for a stranger — an optimistic set that the server actually rejected (an
      expired session, a lapsed purchase, a validation error) would leave the sharer looking
      at a live-looking link and dialog while `GET /share/:slug` 404s for everyone else. See
      the interface doc above.
    */
    try {
      await songsApi.updateSong(id, { visibility });
      return true;
    } catch {
      set((s) => ({
        songs: s.songs.map((song) => (song.id === id ? { ...song, visibility: previous } : song)),
      }));
      return false;
    }
  },

  recordDownload(id, format) {
    set((s) => ({
      songs: s.songs.map((song) =>
        song.id === id ? { ...song, downloadCount: song.downloadCount + 1 } : song
      ),
    }));
    void songsApi.recordDownload(id, format);
  },

  recordPlay(id) {
    /*
      `?? 0` rather than `+ 1` on a required field, because `playCount` is optional: absent
      means "no play data", which is a different claim from zero — see types/domain. The
      first play is what writes the field, so a song nobody has played carries no number at
      all instead of a zero that looks measured.
    */
    set((s) => ({
      songs: s.songs.map((song) =>
        song.id === id ? { ...song, playCount: (song.playCount ?? 0) + 1 } : song
      ),
    }));
    void songsApi.recordPlay(id);
  },

  recordShare(id) {
    // `?? 0` for the same reason as `recordPlay`: `shareCount` is optional, so the first
    // share is what writes the field and an unshared song carries no number at all.
    set((s) => ({
      songs: s.songs.map((song) =>
        song.id === id ? { ...song, shareCount: (song.shareCount ?? 0) + 1 } : song
      ),
    }));
    void songsApi.recordShare(id);
  },

  addVersion(
    songId,
    label,
    kind,
    prompt,
    audioUrl,
    providerClipId,
    createdAt,
    lyrics,
    lyricsTimedRaw,
    lyricTimings,
    lyricSectionTimings
  ) {
    const song = get().songs.find((s) => s.id === songId);
    if (!song) return '';
    // The visible version *number* still counts up for display, but the id is a
    // UUID. Deriving the id from versions.length produced duplicates: with
    // v1/v2/v3, deleting v2 leaves length 2, so the next version was also "v3" —
    // duplicate React keys, and restore/delete/compare resolving to the wrong
    // record.
    const fullLabel = `Version ${song.versions.length + 1} · ${label}`;
    const stampedAt = createdAt ?? new Date().toISOString();
    const version: SongVersion = {
      id: newId(),
      label: fullLabel,
      kind,
      date: shortDate(stampedAt),
      createdAt: stampedAt,
      length: song.length,
      prompt,
      audioUrl,
      providerClipId,
      ...(lyrics !== undefined ? { lyrics } : null),
      ...(lyricsTimedRaw !== undefined ? { lyricsTimedRaw } : null),
      ...(lyricTimings !== undefined ? { lyricTimings } : null),
      ...(lyricSectionTimings !== undefined ? { lyricSectionTimings } : null),
    };
    set((s) => ({
      songs: s.songs.map((x) =>
        x.id === songId ? { ...x, versions: [...x.versions, version] } : x
      ),
    }));
    void songsApi.createVersion(
      songId,
      fullLabel,
      song.length,
      kind,
      prompt,
      audioUrl,
      providerClipId,
      stampedAt,
      lyrics,
      lyricsTimedRaw,
      lyricTimings,
      lyricSectionTimings
    );
    return fullLabel;
  },

  duplicateVersion(songId, versionId) {
    const song = get().songs.find((s) => s.id === songId);
    const source = song?.versions.find((v) => v.id === versionId);
    if (!song || !source) return '';

    /*
      "Version 4 · Copy of Version 2 · Remixed" would be the result of reusing addVersion's
      numbering, which reads as a chain rather than a name. `(copy)` instead, matching
      duplicateSong's title convention so the same gesture reads the same way on both.

      A fresh id, for duplicateSong's reason exactly: sharing one would make deleting the
      copy delete the original, since every operation on a version resolves it by id.
    */
    const copyCreatedAt = new Date().toISOString();
    const copy: SongVersion = {
      ...source,
      id: newId(),
      label: `${source.label} (copy)`,
      // The copy was made now. Carrying the original's date would put a version created
      // this minute at whatever time the original claims, which is the one field a copy
      // must not inherit.
      date: shortDate(copyCreatedAt),
      createdAt: copyCreatedAt,
    };

    set((s) => ({
      songs: s.songs.map((x) => {
        if (x.id !== songId) return x;
        const at = x.versions.findIndex((v) => v.id === versionId);
        const versions = [...x.versions];
        // -1 cannot happen — the source was found above — but splicing at -1 would insert
        // before the last element rather than at the end, so the fallback is explicit.
        versions.splice(at < 0 ? versions.length : at + 1, 0, copy);
        return { ...x, versions };
      }),
    }));
    // The same seam addVersion uses. A real backend would copy the underlying audio here;
    // there is none, so this records the row and nothing more.
    void songsApi.createVersion(
      songId,
      copy.label,
      copy.length,
      copy.kind,
      copy.prompt,
      copy.audioUrl,
      copy.providerClipId,
      copyCreatedAt,
      copy.lyrics,
      copy.lyricsTimedRaw,
      copy.lyricTimings,
      copy.lyricSectionTimings
    );
    return copy.label;
  },

  async saveVersionToLibrary(songId, versionId) {
    const song = get().songs.find((candidate) => candidate.id === songId);
    const version = song?.versions.find((candidate) => candidate.id === versionId);
    if (!song || !version) return null;
    const copy = await songsApi.saveVersionToLibrary(songId, versionId);
    get().insertSong(copy);
    return copy;
  },

  deleteVersion(songId, versionId) {
    set((s) => ({
      songs: s.songs.map((x) =>
        x.id === songId ? { ...x, versions: x.versions.filter((v) => v.id !== versionId) } : x
      ),
    }));
    void songsApi.deleteVersion(songId, versionId);
  },

  renameVersion(songId, versionId, label) {
    set((s) => ({
      songs: s.songs.map((x) =>
        x.id === songId
          ? { ...x, versions: x.versions.map((v) => (v.id === versionId ? { ...v, label } : v)) }
          : x
      ),
    }));
    void songsApi.renameVersion(songId, versionId, label);
  },

  loadVersionForEditing(songId, versionId) {
    const song = get().songs.find((s) => s.id === songId);
    const version = song?.versions.find((v) => v.id === versionId);
    if (!version) return null;
    set({ editingHeadVersionId: versionId });
    return version;
  },

  setFilters(patch) {
    set((s) => ({ filters: { ...s.filters, ...patch } }));
  },

  resetFilters() {
    set({ filters: { ...DEFAULT_FILTERS } });
  },

  async loadSongs() {
    set({ load: LOADING_LOAD });
    try {
      const songs = await songsApi.getSongs();
      // Through `hydrate`, not a bare `set`: hydrate is what advances `seq` past the
      // HIGHEST fetched song. Counting rows is not enough once the backend has gaps from
      // deletions or imports — the next created song would reuse an existing ordering key
      // and the newest/oldest sort would start tying.
      get().hydrate(songs);
      set({ load: READY_LOAD });
    } catch (err) {
      /*
        THE LOCAL LIBRARY IS LEFT ALONE on a failure, which is the whole reason the error
        is state rather than a throw. Clearing the songs would turn a network blip into
        "you have no music" — and the user's library did not go anywhere, the request did.
      */
      set({ load: failedLoad(err) });
    }
  },

  hydrate(songs) {
    const hydrated = hydratePersistedSongLengths(songs);
    set({ songs: hydrated, seq: highestSongSeq(hydrated), editingHeadVersionId: null });
    for (const song of hydrated) {
      if (shouldRefreshGeneratedSong(song)) {
        void reconcileGeneratedSong(song);
      }
    }
  },

  reset() {
    const fresh = USE_MOCK_API ? hydratePersistedSongLengths(mockSeedLibrary()) : [];
    set({
      songs: fresh,
      filters: { ...DEFAULT_FILTERS },
      editingHeadVersionId: null,
      seq: highestSongSeq(fresh),
      generating: false,
      activeGenerationSurface: null,
      // Back to "never asked", matching the fresh seed. A `reset` that left `ready`
      // standing would claim the new fixture came from a server.
      load: IDLE_LOAD,
    });
  },
}));

/** Looks up a song by id. */
export const selectSongById = (songs: Song[], id: string | null): Song | null =>
  (id ? songs.find((s) => s.id === id) : null) ?? null;

/**
 * Applies the search/genre/favourites filters and the sort order.
 *
 * Search covers title, prompt, genre and date. Sorting uses the explicit `seq`
 * counter, not `id` or the display-only `date` string.
 */
export function filterSongs(songs: Song[], filters: SongFilters): Song[] {
  const q = filters.search.toLowerCase();
  return songs
    .filter((s) => {
      const matchesSearch =
        s.title.toLowerCase().includes(q) ||
        (s.prompt || '').toLowerCase().includes(q) ||
        s.genre.toLowerCase().includes(q) ||
        (s.date || '').toLowerCase().includes(q);
      const matchesGenre = filters.genre === 'All genres' || s.genre === filters.genre;
      const matchesFavorites = !filters.favoritesOnly || s.favorite;
      return matchesSearch && matchesGenre && matchesFavorites;
    })
    .sort((a, b) => compareSongsByOrder(a, b, filters.sortOrder));
}
